import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AppointmentScheduleRow, ReportFilters } from '../../../core/models/report.model';
import { PermissionService } from '../../../core/services/permission.service';
import { ReportsService } from '../../../core/services/reports.service';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardActionsComponent } from '../../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../../shared/components/card/card-title.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ReportFilterBarComponent } from '../../../shared/components/report-filter-bar/report-filter-bar.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { DateTimezonePipe } from '../../../shared/pipes/date-timezone.pipe';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { exportToCSV, exportToXLSX, generateFilename } from '../../../shared/utils/export.utils';

/** "Upcoming estimates by day and assignee" (design doc §8). */
@Component({
  selector: 'app-appointment-schedule',
  standalone: true,
  imports: [
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ReportFilterBarComponent,
    SpinnerComponent,
    DateTimezonePipe,
    TranslatePipe,
  ],
  templateUrl: './appointment-schedule.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./appointment-schedule.component.css', '../../../../css/report-print.css'],
})
export class AppointmentScheduleComponent {
  private reportsService = inject(ReportsService);
  private permissionService = inject(PermissionService);

  includeAllUsers = this.permissionService.has('interaction.read-others');
  canExport = this.permissionService.has('report.export');

  rows = signal<AppointmentScheduleRow[]>([]);
  loading = signal(false);

  async onFiltersChange(filters: ReportFilters): Promise<void> {
    this.loading.set(true);
    try {
      this.rows.set(await this.reportsService.getAppointmentSchedule(filters, this.includeAllUsers));
    } finally {
      this.loading.set(false);
    }
  }

  exportCsv(): void {
    exportToCSV(this.exportRows(), generateFilename('appointment-schedule'));
  }

  exportXlsx(): void {
    exportToXLSX(this.exportRows(), generateFilename('appointment-schedule'), 'Appointment Schedule');
  }

  private exportRows(): Record<string, string | number>[] {
    return this.rows().map((r) => ({
      Start: new Date(r.scheduledStart).toISOString(),
      Type: r.appointmentType,
      Status: r.status,
      Assignee: r.assigneeName,
      Contact: r.leadContactName,
      Address: r.addressLabel,
    }));
  }
}
